import type { MetaFunction } from "@remix-run/node";
import { useLoaderData } from "@remix-run/react";
import { useEffect, useRef, useState } from 'react';
import { json } from "@remix-run/node";

// @ts-ignore - タイプ宣言の衝突を解決
declare global {
  interface Window {
    kakao: any;
    ENV: {
      KAKAO_MAP_API_KEY: string;
    };
  }
}

export const meta: MetaFunction = () => {
  return [
    { title: "事務所・所在地 - ソウル江南 | 法務法人フィサン" },
    { name: "description", content: "法務法人フィサンはソウル特別市江南区に位置。地下鉄9号線奉恩寺駅から徒歩2分。日本語対応可能な韓国法律事務所のアクセス情報。" },
    { name: "keywords", content: "法務法人フィサン, 事務所所在地, ソウル江南, アクセス, 連絡先, 日本語対応, 韓国弁護士" },
    { property: "og:title", content: "事務所・所在地 - ソウル江南 | 法務法人フィサン" },
    { property: "og:description", content: "法務法人フィサンはソウル特別市江南区に位置。地下鉄9号線奉恩寺駅から徒歩2分。" },
    { property: "og:type", content: "website" },
    { property: "og:url", content: "https://jp-hwisang.co.kr/ja/office" },
    { property: "og:image", content: "https://jp-hwisang.co.kr/images/jp-hwisang-card.jpg" },
    { property: "og:locale", content: "ja_JP" },
    { name: "twitter:card", content: "summary_large_image" },
    { name: "robots", content: "index, follow" },
  ];
};

export const loader = async () => {
  return json({
    address: {
      main: 'ソウル特別市江南区永東大路611',
      detail: '13階（チャンアンドチャンタワー）',
      postalCode: '06087'
    },
    contact: {
      phone: '+82-2-6959-2170',
      email: 'mgkim@hwisang.co.kr',
      fax: '+82-2-6959-5931'
    },
    transportation: [
      {
        type: '地下鉄',
        items: [
          '9号線 奉恩寺駅 2番出口 徒歩2分',
          '2号線 三成駅 6番出口 徒歩10分'
        ]
      },
      {
        type: 'バス',
        items: [
          '奉恩寺駅停留所: 301, 362, 401, 2415, 3411, 9407, 9507, 9607',
          '三成駅停留所: 143, 146, 333, 341, 401, 2415, 3411, 4318, 9407, 9414, 9607'
        ]
      },
      {
        type: '駐車場',
        items: [
          '駐車可能です（機械式駐車場）'
        ]
      }
    ]
  });
};

export default function JapanOffice() {
  const { address, contact, transportation } = useLoaderData<typeof loader>();
  const mapContainer = useRef<HTMLDivElement>(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);

  // カカオマップのロードと初期化
  useEffect(() => {
    const loadKakaoMap = () => {
      if (typeof window.kakao === 'undefined') {
        const script = document.createElement('script');
        script.async = true;

        // 環境変数からAPIキーを取得
        // @ts-ignore - タイプチェックを無視
        const apiKey = window.ENV?.KAKAO_MAP_API_KEY || '';

        if (!apiKey) {
          console.error('カカオマップAPIキーが設定されていません');
          return;
        }

        script.src = `https://dapi.kakao.com/v2/maps/sdk.js?appkey=${apiKey}&autoload=false`;

        script.onload = () => initializeMap();
        document.head.appendChild(script);
      } else {
        initializeMap();
      }
    };

    const initializeMap = () => {
      if (!mapContainer.current) return;

      if (window.kakao && window.kakao.maps) {
        window.kakao.maps.load(() => {
          const options = {
            center: new window.kakao.maps.LatLng(37.5147252914267, 127.05940565552), // 永東大路611 チャンアンドチャンタワーの正確な座標
            level: 3
          };

          const map = new window.kakao.maps.Map(mapContainer.current, options);

          // マーカーを追加
          const marker = new window.kakao.maps.Marker({
            position: map.getCenter()
          });
          marker.setMap(map);

          // 情報ウィンドウを追加
          const infowindow = new window.kakao.maps.InfoWindow({
            content: '<div class="p-2 text-center font-medium text-gray-800">法務法人フィサン</div>'
          });
          infowindow.open(map, marker);

          // 地図コントロールを追加
          const zoomControl = new window.kakao.maps.ZoomControl();
          map.addControl(zoomControl, window.kakao.maps.ControlPosition.RIGHT);

          setIsMapLoaded(true);
        });
      }
    };

    // ページロード時に一度だけ実行
    const timer = setTimeout(loadKakaoMap, 1000); // わずかな遅延で初期化の問題を防ぐ

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="container mx-auto px-4 py-4">
          {/* Mobile: 2-row layout, Desktop: single row */}
          <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
            {/* Logo - centered on mobile, left on desktop */}
            <a href="/ja" className="flex items-center justify-center md:justify-start">
              <img src="/images/no-padding-logo.png" alt="法務法人フィサン" className="h-8 w-auto" />
            </a>

            {/* Nav and Language button - centered on mobile */}
            <div className="flex items-center justify-center md:justify-end gap-4 md:gap-6">
              <nav className="flex items-center gap-4 md:gap-6 text-sm">
                <a href="/ja/office" className="text-gray-700 hover:text-black whitespace-nowrap">事務所・所在地</a>
                <a href="/ja/services" className="text-gray-700 hover:text-black whitespace-nowrap">業務分野</a>
                <a href="/ja/blog" className="text-gray-700 hover:text-black whitespace-nowrap">法律ブログ</a>
              </nav>
              <a
                href="https://jp-hwisang.co.kr"
                target="_blank"
                rel="noopener noreferrer"
                className="px-3 py-1.5 border border-gray-300 hover:border-gray-900 text-gray-700 hover:text-black text-sm transition-colors whitespace-nowrap"
              >
                한국어
              </a>
            </div>
          </div>
        </div>
      </header>

      {/* Title Section */}
      <section className="bg-gradient-to-br from-[#b98e6a]/5 to-[#a67c5a]/3 py-16">
        <div className="container mx-auto px-4">
          <a href="/ja" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6 text-sm transition-colors">
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            ホームに戻る
          </a>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-gray-900">事務所・所在地</h1>
          <p className="text-xl text-gray-700">法務法人フィサンへのアクセス方法をご案内します</p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Map Section */}
            <div className="bg-white rounded-lg shadow-lg overflow-hidden">
              <div className="w-full h-full min-h-[400px] lg:min-h-[600px]">
                <div ref={mapContainer} className="w-full h-full relative">
                  {!isMapLoaded && (
                    <div className="absolute inset-0 flex items-center justify-center bg-gray-200">
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-2"></div>
                        <p className="text-blue-600">地図を読み込んでいます...</p>
                        <p className="text-gray-500 text-sm mt-1">少々お待ちください</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Information Section */}
            <div className="space-y-8">
              {/* Address */}
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold mb-3 text-gray-900">住所</h2>
                    <p className="text-gray-700 mb-2">{address.main}</p>
                    <p className="text-gray-700 mb-2">{address.detail}</p>
                    <p className="text-gray-500 text-sm">郵便番号: {address.postalCode}</p>
                  </div>
                </div>
              </div>

              {/* Contact */}
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold mb-3 text-gray-900">連絡先</h2>
                    <div className="space-y-2">
                      <div>
                        <span className="text-gray-500">電話:</span>
                        <a href={`tel:${contact.phone}`} className="text-gray-700 ml-2 hover:text-blue-600">{contact.phone}</a>
                      </div>
                      <div>
                        <span className="text-gray-500">FAX:</span>
                        <span className="text-gray-700 ml-2">{contact.fax}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Email:</span>
                        <a href={`mailto:${contact.email}`} className="text-gray-700 ml-2 hover:text-blue-600">{contact.email}</a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Transportation */}
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-start mb-4">
                  <div className="bg-blue-100 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold mb-4 text-gray-900">交通アクセス</h2>

                    {transportation.map((transport, idx) => (
                      <div key={idx} className={`mb-4 ${idx !== transportation.length - 1 ? 'border-b border-gray-200 pb-4' : ''}`}>
                        <h3 className="text-lg font-semibold text-gray-800 mb-2 flex items-center">
                          {transport.type === '地下鉄' && (
                            <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path d="M8 16V7a4 4 0 0 1 8 0v9M6 21h12a2 2 0 0 0 2-2v-6.26A2 2 0 0 0 19 11h-1V7a5 5 0 0 0-10 0v4H7a2 2 0 0 0-1 3.74V19a2 2 0 0 0 2 2z" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                          {transport.type === 'バス' && (
                            <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path d="M4 11V7a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v4" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                              <path d="M4 14v3a2 2 0 0 0 2 2h1" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                          {transport.type === '駐車場' && (
                            <svg className="w-5 h-5 mr-2 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path d="M10 9h4a2 2 0 1 0 0-4h-4v8" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                          )}
                          {transport.type}
                        </h3>
                        <ul className="space-y-1 ml-7 text-gray-700">
                          {transport.items.map((item, itemIdx) => (
                            <li key={itemIdx} className="flex items-start">
                              <span className="inline-block w-1.5 h-1.5 rounded-full bg-blue-600 mt-2 mr-2"></span>
                              <span>{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <div className="flex items-start">
                  <div className="bg-blue-100 p-3 rounded-full mr-4">
                    <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold mb-3 text-gray-900">営業時間</h2>
                    <div className="space-y-2 text-gray-700">
                      <p><span className="font-medium">平日:</span> 午前9:00 - 午後6:00</p>
                      <p><span className="font-medium">昼休み:</span> 午後12:00 - 午後1:00</p>
                      <p><span className="font-medium">週末・祝日:</span> 休業</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center md:text-left">
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">法務法人フィサン</h5>
              <p className="text-sm text-gray-600 leading-relaxed">
                韓国を代表する法律事務所として、<br />
                専門的で信頼できる法律サービスを提供します
              </p>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">業務分野</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>刑事事件</li>
                <li>金融・不動産</li>
                <li>経営権紛争</li>
                <li>家事事件</li>
              </ul>
            </div>
            <div>
              <h5 className="text-base font-medium mb-4 text-gray-900">お問い合わせ</h5>
              <ul className="space-y-2 text-sm text-gray-600">
                <li>電話: +82-2-6959-2170</li>
                <li>Email: mgkim@hwisang.co.kr</li>
                <li>住所: ソウル特別市江南区永東大路611, 13階</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-200 mt-8 pt-8 text-center">
            <p className="text-xs text-gray-500">&copy; 2024 法務法人フィサン. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
